import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Minus, Trash2, ArrowRight, Loader2 } from "lucide-react";
import { useCart } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { readRefCode } from "@/components/RefTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Cart() {
  const { items, updateQty, removeItem, replaceCart } = useCart();
  const { user, authHeader } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalog, setCatalog] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [recoveredNotice, setRecoveredNotice] = useState("");

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setCatalog(r.data));
  }, []);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  // Abandoned cart recovery — if ?recover=SID present, fetch and repopulate
  useEffect(() => {
    const sid = searchParams.get("recover");
    if (!sid) return;
    setRecovering(true);
    axios
      .get(`${API}/checkout/recover/${sid}`)
      .then((r) => {
        if (r.data.items && r.data.items.length) {
          replaceCart(r.data.items);
          if (r.data.customer_email) setEmail(r.data.customer_email);
          setRecoveredNotice("We restored the items you had in your cart. Ready when you are.");
        }
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not restore that cart");
      })
      .finally(() => {
        setRecovering(false);
        // Strip ?recover from the URL for cleanliness
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("recover");
        setSearchParams(nextParams, { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cartItems = items
    .map((it) => {
      const p = catalog.find((c) => c.id === it.sku);
      if (!p) return null;
      return { ...it, ...p, line_total: p.unit_price * it.quantity };
    })
    .filter(Boolean);

  const subtotal = cartItems.reduce((acc, i) => acc + i.line_total, 0);
  const discount = coupon ? Number(coupon.discount_amount || 0) : 0;
  const subtotalAfter = Math.max(0, subtotal - discount);

  // Re-validate coupon whenever subtotal changes (avoid it becoming invalid due to min_subtotal)
  useEffect(() => {
    if (!coupon) return;
    let cancelled = false;
    axios.post(`${API}/coupons/validate`, { code: coupon.code, subtotal })
      .then((r) => { if (!cancelled) setCoupon(r.data); })
      .catch(() => { if (!cancelled) { setCoupon(null); setCouponError("Coupon no longer applies at this subtotal."); } });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal]);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) return;
    setCouponError("");
    setCouponLoading(true);
    try {
      const { data } = await axios.post(`${API}/coupons/validate`, { code, subtotal });
      setCoupon(data);
      setCouponInput("");
    } catch (e) {
      setCouponError(e.response?.data?.detail || "Invalid coupon.");
      setCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => { setCoupon(null); setCouponError(""); };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/checkout/session`,
        {
          items: items.map(({ sku, quantity }) => ({ sku, quantity })),
          origin_url: window.location.origin,
          customer_email: email || null,
          coupon_code: coupon ? coupon.code : null,
          ref_code: readRefCode(),
        },
        { headers: authHeader() }
      );
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="cart-page" className="bg-[#050505] text-white pt-32 min-h-screen">
      <div className="px-6 md:px-12 lg:px-24 pb-24">
        <p className="eyebrow mb-4">Your Order</p>
        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight mb-12">
          Cart
        </h1>

        {recovering && (
          <div className="border border-white/10 bg-white/[0.02] p-4 mb-8 flex items-center gap-3 text-sm text-zinc-400">
            <Loader2 className="w-4 h-4 animate-spin" /> Restoring your cart…
          </div>
        )}
        {recoveredNotice && (
          <div
            data-testid="cart-recovered-notice"
            className="border border-[#E8A851]/40 bg-[#E8A851]/[0.05] p-4 mb-8 text-sm text-[#E8A851]"
          >
            {recoveredNotice}
          </div>
        )}

        {cartItems.length === 0 ? (
          <div
            data-testid="empty-cart-state"
            className="border border-white/10 p-12 bg-white/[0.02] text-center"
          >
            <p className="text-zinc-400 mb-6">Your cart is empty.</p>
            <Link to="/product" data-testid="empty-cart-shop-link" className="btn-primary">
              Shop Aura Sleeve <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-3">
              {cartItems.map((it) => (
                <div
                  key={it.sku}
                  data-testid={`cart-line-${it.sku}`}
                  className="border border-white/10 p-6 flex items-center justify-between gap-6 bg-white/[0.02]"
                >
                  <div className="flex-1">
                    <p className="eyebrow mb-2">
                      {it.id === "aura-bundle-3" ? "Bundle of 3" : "Single"}
                    </p>
                    <p className="font-display text-xl font-medium">{it.name}</p>
                    <p className="text-sm text-zinc-500 mt-1">
                      ${it.unit_price.toFixed(2)} each
                    </p>
                  </div>

                  <div className="flex items-center border border-white/20">
                    <button
                      data-testid={`cart-decrement-${it.sku}`}
                      onClick={() => updateQty(it.sku, it.quantity - 1)}
                      className="p-2 hover:bg-white/5"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="px-4 font-display">{it.quantity}</span>
                    <button
                      data-testid={`cart-increment-${it.sku}`}
                      onClick={() => updateQty(it.sku, it.quantity + 1)}
                      className="p-2 hover:bg-white/5"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="text-right min-w-[100px]">
                    <p className="font-display text-xl font-semibold">
                      ${it.line_total.toFixed(2)}
                    </p>
                  </div>

                  <button
                    data-testid={`cart-remove-${it.sku}`}
                    onClick={() => removeItem(it.sku)}
                    className="p-2 hover:bg-white/5 text-zinc-400 hover:text-white"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="border border-white/10 p-8 bg-white/[0.02] h-fit sticky top-32">
              <p className="eyebrow mb-6">Summary</p>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Subtotal</span>
                  <span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                {coupon && (
                  <div className="flex justify-between text-sm">
                    <span className="text-[#E8A851]" data-testid="cart-coupon-line">
                      Coupon ({coupon.code})
                    </span>
                    <span className="text-[#E8A851]" data-testid="cart-discount-amount">
                      −${discount.toFixed(2)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Shipping</span>
                  <span className="text-zinc-500">Selected at checkout</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Tax</span>
                  <span className="text-zinc-500">Calculated at checkout</span>
                </div>
              </div>

              <div className="border-t border-white/10 pt-4 mb-8 flex justify-between items-baseline">
                <span className="text-sm uppercase tracking-[0.15em]">Subtotal</span>
                <span data-testid="cart-total" className="font-display text-2xl font-semibold">
                  ${subtotalAfter.toFixed(2)}
                </span>
              </div>

              {/* Coupon input */}
              <div className="mb-6">
                <label className="eyebrow mb-2 block">Coupon code</label>
                {coupon ? (
                  <div
                    data-testid="cart-coupon-applied"
                    className="flex items-center justify-between gap-3 border border-[#E8A851]/40 bg-[#E8A851]/[0.05] px-3 py-2"
                  >
                    <span className="text-sm text-[#E8A851] font-mono">{coupon.code}</span>
                    <button
                      data-testid="cart-remove-coupon-button"
                      onClick={removeCoupon}
                      className="text-xs uppercase tracking-[0.15em] text-zinc-400 hover:text-white"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="cart-coupon-input"
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())}
                      placeholder="WORSHIP15"
                      className="flex-1 bg-transparent border-b border-white/20 focus:border-white text-white px-0 py-2 text-sm uppercase tracking-wider focus:outline-none"
                    />
                    <button
                      data-testid="cart-apply-coupon-button"
                      onClick={applyCoupon}
                      disabled={couponLoading || !couponInput.trim()}
                      className="text-xs uppercase tracking-[0.15em] border border-white/20 px-4 hover:bg-white/5 disabled:opacity-40"
                    >
                      {couponLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Apply"}
                    </button>
                  </div>
                )}
                {couponError && (
                  <p data-testid="cart-coupon-error" className="text-xs text-red-400 mt-2">
                    {couponError}
                  </p>
                )}
              </div>

              <div className="mb-6">
                <label className="eyebrow mb-2 block">
                  Email {user ? "" : "(optional)"}
                </label>
                <input
                  type="email"
                  data-testid="checkout-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@church.org"
                  className="w-full bg-transparent border-b border-white/20 focus:border-white text-white px-0 py-3 placeholder:text-white/30 focus:outline-none"
                />
                {!user && (
                  <p className="text-xs text-zinc-500 mt-3">
                    <Link to="/login" className="text-white underline">Sign in</Link>
                    {" "}or{" "}
                    <Link to="/signup" className="text-white underline">create an account</Link>
                    {" "}to track your order.
                  </p>
                )}
              </div>

              <button
                data-testid="checkout-button"
                disabled={loading}
                onClick={handleCheckout}
                className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Redirecting…
                  </>
                ) : (
                  <>
                    Secure Checkout <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="text-xs text-zinc-500 mt-6 text-center leading-relaxed">
                Powered by Stripe. Apple Pay &amp; Google Pay supported.
                PayPal, Braintree &amp; Amazon Pay coming soon.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
